/**
 * layoutAtoms - Jotai atoms backing the incremental radial layout for the
 * simple-home cypher exploration graph.
 *
 * These atoms own the *positional* + *display* state of the graph. All the
 * heavy lifting around Cypher generation, bridge handling, parsing, and
 * hydration lives in the ported discovery stack (`explorationActions.ts` →
 * `DiscoverForNodeAtom` → `cypherQueryService`).
 *
 * Responsibilities of this module, in order:
 *
 *   1. `InitRadialLayoutAtom(userAri)` — seeds the graph with the root user
 *      node plus its first-ring children. Dispatches `DiscoverForNodeAtom`
 *      for a full bridge-aware discovery, runs `scoreForYou` as the sole
 *      Mode A filter, converts survivors to `DisplayNode`, and drops them
 *      into the layout via `initLayout`.
 *
 *   2. `ExpandNodeAtom(nodeId)` — same pipeline for Mode B: dispatch, run
 *      `scoreForYou` (when the parent is a user) or `scoreExplore`
 *      (otherwise), materialise `DisplayNode`s, splice the kept children
 *      into the layout via `expandLayout`.
 *
 *   3. `CollapseNodeAtom(nodeId)` — removes the node's descendants.
 *
 *   4. `ExpandClusterAtom(clusterId)` — promotes a cluster node's hidden
 *      members back into individual layout entries.
 *
 *   5. `HydratedNodesAtom` — single display-layer lookup keyed by ARI. The
 *      discovery stack already hydrates nodes in-place (merged, bridge-aware),
 *      so there is no second "hydrate" pass; this atom is populated during
 *      the same orchestration tick as the layout delta.
 */

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

import { DiscoverForNodeAtom } from '../actions/explorationActions';
import {
	expandLayout,
	initLayout,
	type ChildSpec,
	type LayoutDelta,
	type LayoutNode,
} from '../layout/IncrementalRadialLayout';
import type { NodeSize } from '../layout/RadialLayoutEngine';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import { estimateLayoutNodeSize } from '../nodeSizing';
import { extractRelationshipVerb } from '../panels/relationshipDisplayUtils';
import {
	CURRENT_USER_ARI_PROXY,
	type DiscoveredNode,
	type RelationshipInfo,
} from '../services/cypherQueryService';
import type { DisplayNode } from '../services/displayNode';
import { placeholderDisplayNode, toDisplayNode } from '../services/displayNode';
import type { Candidate, ScoredNode } from '../services/nodeSelectionService';
import { scoreExplore, scoreForYou } from '../services/nodeSelectionService';
import { AtiNodeMetadataMapAtom, twgRelationshipsAsyncAtom } from '../services/twgSchemaService';
import { isUserTypename } from '../services/userTypes';
import { resolveNodeTypenamesFromAris } from '../utils/ariUtils';

import {
	DiscoveredIncomingAtom,
	DiscoveredOutgoingAtom,
	DiscoveryStatusAtom,
} from './explorationAtoms';
import {
	NodeRecencyAtom,
	HiddenNodesAtom,
	EvaluateOverlapHidesAtom,
	MarkAppearingAtom,
	AppearingNodesAtom,
} from './recencyAtoms';

// ============================================================================
// Types
// ============================================================================

/** A cross-edge emitted on dedup — same-target-via-multiple-parents. */
export type CrossEdge = {
	from: string;
	to: string;
	relType?: string;
	/**
	 * Human-readable verb for the edge label (e.g. "viewed", "owns").
	 * Surfaced on `FloatingEdge` via `data.label` so cross-edges between
	 * already-placed nodes carry the same label as parent→child edges.
	 */
	label?: string;
};

/** Status of the radial layout bootstrap. */
export type LayoutStatus = 'idle' | 'probing-mode-a' | 'ready' | 'expanding' | 'error';

// ============================================================================
// Source atoms
// ============================================================================

/** The positioned layout nodes keyed by ARI. Source of truth for geometry. */
export const LayoutNodesAtom: PrimitiveAtom<Map<string, LayoutNode>> & {
	init: Map<string, LayoutNode>;
} = atom<Map<string, LayoutNode>>(new Map());

/** Cross-edges between already-placed nodes (dedup hits). */
export const CrossEdgesAtom: PrimitiveAtom<CrossEdge[]> & {
	init: CrossEdge[];
} = atom<CrossEdge[]>([]);

/**
 * Display lookup: ARI → fully-resolved `DisplayNode` (typename display,
 * product logo, icon, etc.). Populated by the orchestrator atoms after the
 * ported discovery pipeline returns hydrated `DiscoveredNode`s.
 */
export const HydratedNodesAtom: PrimitiveAtom<Map<string, DisplayNode>> & {
	init: Map<string, DisplayNode>;
} = atom<Map<string, DisplayNode>>(new Map());

/** Top-level bootstrap status. */
export const LayoutStatusAtom: PrimitiveAtom<LayoutStatus> & {
	init: LayoutStatus;
} = atom<LayoutStatus>('idle');

/** Error message (if any). */
const LayoutErrorAtom = atom<string | null>(null);

/** Set of ARIs currently being expanded (guards against duplicate clicks). */
export const ExpandingNodeIdsAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom<Set<string>>(new Set<string>());

/** Set of ARIs that have already been successfully expanded. */
export const ExpandedNodeIdsAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom<Set<string>>(new Set<string>());

// ============================================================================
// Candidate flattening — converts discovered relationships into the per-node
// shape scoreForYou / scoreExplore expect.
// ============================================================================

/**
 * Pulls an activity timestamp from a discovered node's normalised properties.
 * Falls back through common aliases (`lastUpdated` → `updated` → `updatedAt`
 * → `created`) so the recency decay has something to chew on even when the
 * 3P connector uses a non-canonical field name.
 */
function extractActivity(node: DiscoveredNode): string | number | null {
	const p = node.properties ?? {};
	const raw = p.lastUpdated ?? p.updated ?? p.updatedAt ?? p.created ?? null;
	if (typeof raw === 'string' || typeof raw === 'number') {
		return raw;
	}
	return null;
}

/**
 * Returns the canonical Loom video id for a discovered node iff the node
 * represents a Loom recording, otherwise `null`.
 *
 * Loom recordings reach the graph through two distinct typenames:
 *   • `LoomVideo` — first-party Loom representation. The Loom video id is
 *     the trailing path segment of its ARI:
 *       `ari:cloud:loom:<workspace>:video/activation/<actor>/<videoId>`
 *   • `ExternalVideo` (with `ari:cloud:graph::video/...`) — the same video
 *     bridged through the third-party graph. **Only** ExternalVideos whose
 *     `properties.thirdPartyId` is Loom-scoped (`ari:third-party:loom.loom::video/<videoId>`)
 *     are eligible for Loom dedup; every other ExternalVideo (Vimeo,
 *     YouTube, etc.) returns `null` here and is therefore skipped by the
 *     dedup pass in `flattenToCandidates`.
 *
 * The trailing `<videoId>` is the comparison key shared by both shapes.
 */
function getLoomVideoIdFromNode(node: DiscoveredNode): string | null {
	if (node.typename === 'LoomVideo') {
		const m = node.id.match(/\/([0-9a-fA-F]{16,})$/);
		return m ? m[1] : null;
	}
	if (node.typename === 'ExternalVideo') {
		const tpid = node.properties?.thirdPartyId;
		if (typeof tpid !== 'string') {
			return null;
		}
		// Gate: only Loom-scoped third-party ids participate in dedup. All
		// other connectors' ExternalVideos pass through untouched.
		if (!tpid.startsWith('ari:third-party:loom.loom::video/')) {
			return null;
		}
		const m = tpid.match(/\/video\/([0-9a-fA-F]{16,})$/);
		return m ? m[1] : null;
	}
	return null;
}

/**
 * Flattens the combined outgoing + incoming discovery result into a
 * per-ARI candidate list. When the same neighbour surfaces via multiple
 * relationship types (or in both directions), we accumulate rel types,
 * directions, and signals so the scorer sees a single row per node.
 *
 * Loom dedup: when the same Loom recording surfaces via both the
 * first-party `LoomVideo` typename **and** the third-party-bridged
 * `ExternalVideo` typename, we keep the `LoomVideo` representation
 * and drop the `ExternalVideo` one. Loom is treated as a first-party
 * surface, and showing the same recording twice (once per surface)
 * inflates the visible video count and crowds out other content.
 */
function flattenToCandidates(
	outgoing: RelationshipInfo[],
	incoming: RelationshipInfo[],
	relMap: Record<string, any>,
	parentAri?: string,
): Candidate[] {
	// First pass over both directions: collect every Loom video id that
	// has a first-party `LoomVideo` representation. Any `ExternalVideo`
	// that shares one of these ids (via its loom-scoped `thirdPartyId`)
	// will be dropped in the main pass. `getLoomVideoIdFromNode` already
	// gates on Loom-scoped third-party ids, so non-Loom ExternalVideos
	// (Vimeo / YouTube / etc.) never participate in dedup.
	const loomVideoIds = new Set<string>();
	for (const rel of outgoing) {
		for (const node of rel.nodes ?? []) {
			if (node.typename === 'LoomVideo') {
				const id = getLoomVideoIdFromNode(node);
				if (id) {
					loomVideoIds.add(id);
				}
			}
		}
	}
	for (const rel of incoming) {
		for (const node of rel.nodes ?? []) {
			if (node.typename === 'LoomVideo') {
				const id = getLoomVideoIdFromNode(node);
				if (id) {
					loomVideoIds.add(id);
				}
			}
		}
	}

	const byAri = new Map<string, Candidate>();

	const ingest = (rels: RelationshipInfo[], direction: 'in' | 'out') => {
		for (const rel of rels) {
			const baseRelType = rel.sourceRelType ?? rel.type;
			const verb = extractRelationshipVerb(baseRelType, relMap);
			for (const node of rel.nodes ?? []) {
				// Skip self-loops where discovery surfaced the parent itself
				// as a related node — these would render as orphan edges
				// pointing back to the same node.
				if (parentAri && node.id === parentAri) {
					continue;
				}
				// Loom dedup: drop the third-party-bridged ExternalVideo if
				// its underlying Loom id already arrived via LoomVideo. Loom
				// is treated as the canonical first-party surface.
				// `getLoomVideoIdFromNode` returns null for any ExternalVideo
				// that isn't Loom-bridged, so non-Loom external videos pass
				// through this guard untouched.
				if (node.typename === 'ExternalVideo') {
					const loomId = getLoomVideoIdFromNode(node);
					if (loomId && loomVideoIds.has(loomId)) {
						continue;
					}
				}
				const productDisplayName =
					typeof node.properties?.productDisplayName === 'string'
						? node.properties.productDisplayName
						: undefined;
				// Carry forward the third-party identifier so downstream
				// scoring (specifically the 3P rescue) can recognise
				// bitbucket-bridged External nodes and exclude them from
				// the 3P quota. Prefer `thirdPartyId` and fall back to
				// `thirdPartyAri` — both shapes appear in the wild
				// depending on the discovery path.
				const thirdPartyHint =
					(typeof node.properties?.thirdPartyId === 'string' && node.properties.thirdPartyId) ||
					(typeof node.properties?.thirdPartyAri === 'string' && node.properties.thirdPartyAri) ||
					null;
				// Pull the per-rel-edge timestamp surfaced by parseDiscoveryResult.
				// Falls through to undefined for legacy / bridge rows that did
				// not project relLastUpdated. When present, this is much more
				// reliable than the node's own lastUpdated for "what did the
				// user actually do recently" — see relevancy-analysis.md.
				const relTs = rel.lastUpdatedByNodeId?.[node.id];
				const existing = byAri.get(node.id);
				if (existing) {
					if (!existing.relTypes.includes(rel.type)) {
						existing.relTypes.push(rel.type);
					}
					// Track the canonical verb for this rel so the scoring
					// layer can decide whether the rel is active without
					// re-running the registry-dependent verb extractor.
					if (!existing.verbsByRelType) existing.verbsByRelType = new Map();
					existing.verbsByRelType.set(rel.type, verb);
					if (!existing.directions.includes(direction)) {
						existing.directions.push(direction);
					}
					if (existing.signals && !existing.signals.includes(verb)) {
						existing.signals.push(verb);
					}
					// Later activity wins
					const act = extractActivity(node);
					if (act != null && (existing.lastActivity == null || act > existing.lastActivity)) {
						existing.lastActivity = act;
					}
					if (!existing.integration && productDisplayName) {
						existing.integration = productDisplayName;
					}
					if (!existing.thirdPartyHint && thirdPartyHint) {
						existing.thirdPartyHint = thirdPartyHint;
					}
					// Track per-verb rel timestamps (and the max across verbs)
					// so scoring can prefer the rel timestamp over the node's
					// own lastUpdated when present.
					if (relTs != null) {
						if (!existing.relLastUpdatedBySignal) {
							existing.relLastUpdatedBySignal = new Map();
						}
						const prevVerb = existing.relLastUpdatedBySignal.get(verb);
						if (prevVerb == null || relTs > prevVerb) {
							existing.relLastUpdatedBySignal.set(verb, relTs);
						}
						if (existing.maxRelLastUpdated == null || relTs > existing.maxRelLastUpdated) {
							existing.maxRelLastUpdated = relTs;
						}
					}
				} else {
					const relLU = relTs != null ? new Map<string, number>([[verb, relTs]]) : undefined;
					byAri.set(node.id, {
						ari: node.id,
						typename: node.typename,
						relTypes: [rel.type],
						directions: [direction],
						lastActivity: extractActivity(node),
						integration: productDisplayName,
						signals: [verb],
						thirdPartyHint,
						relLastUpdatedBySignal: relLU,
						maxRelLastUpdated: relTs ?? null,
						verbsByRelType: new Map([[rel.type, verb]]),
					});
				}
			}
		}
	};

	ingest(outgoing, 'out');
	ingest(incoming, 'in');

	return Array.from(byAri.values());
}

// ============================================================================
// Delta merging
// ============================================================================

/**
 * Merge a delta into the layout map and cross-edge list. Returns new maps
 * so Jotai dependents see identity changes.
 */
function applyDelta(
	current: Map<string, LayoutNode>,
	currentCross: CrossEdge[],
	delta: LayoutDelta,
): { nodes: Map<string, LayoutNode>; crossEdges: CrossEdge[] } {
	const nodes = new Map(current);
	for (const n of delta.added) {
		nodes.set(n.id, n);
	}
	for (const r of delta.removed) {
		nodes.delete(r);
	}
	// Apply soft-resolution position updates to existing nodes. The
	// magnetic-spring hook (`useRadialMagneticLayout::layoutNodes effect`)
	// detects the base-position change and seeds `disp` so the node
	// visually stays in place for one frame, then springs to the new
	// rest. The result is a smooth nudge, not a snap.
	for (const u of delta.updated) {
		const existing = nodes.get(u.id);
		if (existing) {
			nodes.set(u.id, { ...existing, x: u.x, y: u.y });
		}
	}
	const crossEdges = [...currentCross];
	for (const e of delta.crossEdges) {
		// Dedup by (from, to) so re-discoveries of the same pair don't
		// stack duplicate edges. If a later discovery carries a label
		// while the existing entry doesn't, upgrade the existing entry
		// in-place instead of pushing a duplicate.
		const existingIdx = crossEdges.findIndex(
			(existing) => existing.from === e.from && existing.to === e.to,
		);
		if (existingIdx >= 0) {
			const existing = crossEdges[existingIdx];
			if (!existing.label && e.label) {
				crossEdges[existingIdx] = {
					...existing,
					label: e.label,
					relType: e.relType ?? existing.relType,
				};
			}
			continue;
		}
		crossEdges.push({ from: e.from, to: e.to, relType: e.relType, label: e.label });
	}
	return { nodes, crossEdges };
}

/**
 * Merge a batch of hydrated discovery nodes into `HydratedNodesAtom` as
 * `DisplayNode`s. Existing entries survive so placeholders stay readable
 * if the incoming node happens to have fewer populated fields.
 */
function mergeDisplayNodes(
	set: any,
	get: any,
	discovered: DiscoveredNode[],
	typenameDisplayMap: Record<string, string> | undefined,
) {
	if (discovered.length === 0) {
		return;
	}
	const prev = get(HydratedNodesAtom) as Map<string, DisplayNode>;
	const next = new Map(prev);
	for (const node of discovered) {
		next.set(node.id, toDisplayNode(node, { typenameDisplayMap }));
	}
	set(HydratedNodesAtom, next);
}

/**
 * Upsert a single `DisplayNode` into `HydratedNodesAtom`. Used for the
 * root user node which isn't part of the discovery payload.
 */
function setDisplayNode(set: any, get: any, display: DisplayNode) {
	const prev = get(HydratedNodesAtom) as Map<string, DisplayNode>;
	const next = new Map(prev);
	next.set(display.id, display);
	set(HydratedNodesAtom, next);
}

// ============================================================================
// Layout-children helper — maps `ScoredNode` → `ChildSpec` using the
// display-layer data already materialised into `HydratedNodesAtom`.
// ============================================================================

function toLayoutChildren(scored: ScoredNode[], hydrated: Map<string, DisplayNode>): ChildSpec[] {
	return scored.map((s) => {
		const display = hydrated.get(s.ari);
		// `signals[0]` is the humanized verb produced by `extractRelationshipVerb`
		// in `flattenToCandidates` — the first relationship that surfaced this
		// node. Carry it through so the renderer can show it on the edge.
		const parentRelLabel = s.signals?.[0];
		return {
			id: s.ari,
			score: s.score,
			typename: s.typename,
			integration: s.integration,
			parentRelLabel,
			sizingInput: {
				typename: s.typename,
				title: display?.displayName ?? null,
				isPlaceholder: !display,
			},
		};
	});
}

// ============================================================================
// Init — Mode A "For You" landing load
// ============================================================================

/**
 * Bootstrap the radial layout for a given user ARI. Delegates the heavy
 * lifting to the ported `DiscoverForNodeAtom` then runs the relevancy
 * algorithm as a post-fetch filter before seeding `LayoutNodesAtom`.
 */
export const InitRadialLayoutAtom: WritableAtom<
	null,
	[userInput: string | ObjectNodeData],
	Promise<void>
> & {
	init: null;
} = atom(null, async (get, set, userInput: string | ObjectNodeData) => {
	if (get(LayoutStatusAtom) === 'ready') {
		return;
	}
	set(LayoutStatusAtom, 'probing-mode-a');
	set(LayoutErrorAtom, null);

	try {
		const userAri = typeof userInput === 'string' ? userInput : userInput.id;
		const atiMap = get(AtiNodeMetadataMapAtom);
		const userTypename =
			atiMap && typeof userAri === 'string' && userAri !== CURRENT_USER_ARI_PROXY
				? (resolveNodeTypenamesFromAris([userAri], atiMap)[0] ?? 'AtlassianUser')
				: 'AtlassianUser';

		const userNodeData: ObjectNodeData = {
			id: userAri,
			typename: userTypename,
			displayName: 'You',
			url: null,
			properties: typeof userInput === 'string' ? {} : userInput.properties,
		};

		// Full bridge-aware discovery via the ported pipeline. This
		// writes into DiscoveredOutgoingAtom / DiscoveredIncomingAtom.
		const discoveryResult = await set(DiscoverForNodeAtom, userNodeData);

		// Bail out if the caller never completed discovery cleanly.
		if (get(DiscoveryStatusAtom) === 'error') {
			set(LayoutStatusAtom, 'error');
			set(LayoutErrorAtom, 'Discovery failed');
			return;
		}

		const rootNode = discoveryResult?.rootNode;
		const rootAri = rootNode?.id ?? userAri;
		const rootTypename = rootNode?.typename ?? userTypename;
		const rootTitle = rootNode?.displayName ?? 'You';

		setDisplayNode(
			set,
			get,
			rootNode
				? toDisplayNode(rootNode, { typenameDisplayMap: undefined })
				: placeholderDisplayNode(rootAri, rootTypename),
		);

		const outgoing = get(DiscoveredOutgoingAtom) as RelationshipInfo[];
		const incoming = get(DiscoveredIncomingAtom) as RelationshipInfo[];
		const relMap = await get(twgRelationshipsAsyncAtom);

		// Stash every hydrated neighbour as a DisplayNode first so the
		// layout-children step can read titles out of the cache.
		const allDiscovered: DiscoveredNode[] = [];
		for (const rel of [...outgoing, ...incoming]) {
			for (const n of rel.nodes ?? []) {
				allDiscovered.push(n);
			}
		}
		mergeDisplayNodes(set, get, allDiscovered, undefined);

		// Algorithm filter.
		const candidates = flattenToCandidates(outgoing, incoming, relMap, rootAri).map((c) => ({
			...c,
			signals: c.signals ?? [],
		}));
		// Initial radial view caps related entities at a focused dozen so
		// the user sees a digestible set upfront. Expansion / exploration
		// can add more nodes after interaction.
		const selection = scoreForYou(candidates, { totalSlots: 12 });

		const hydrated = get(HydratedNodesAtom) as Map<string, DisplayNode>;
		const children = toLayoutChildren(selection.kept, hydrated);

		const nodes = initLayout(
			{
				id: rootAri,
				sizingInput: {
					typename: rootTypename,
					title: rootTitle,
					isPlaceholder: false,
				},
			},
			children,
		);
		set(LayoutNodesAtom, nodes);
		set(CrossEdgesAtom, []);

		// Stamp every freshly-placed node with the current timestamp
		// so the recency map always has a baseline for overlap-hide
		// comparisons in subsequent expansions.
		const now = Date.now();
		const recency = new Map(get(NodeRecencyAtom));
		for (const id of nodes.keys()) {
			recency.set(id, now);
		}
		set(NodeRecencyAtom, recency);

		// Mark every first-paint node as "appearing" so they fade in
		// (opacity 0 → 1) rather than popping into place.
		set(MarkAppearingAtom, Array.from(nodes.keys()));

		// Mark the user (root) as already expanded. Discovery + scoring
		// just ran via `scoreForYou` to populate the user's neighbours
		// in the radial layout — we do *not* want a subsequent click on
		// the user to re-trigger `ExpandNodeAtom` (which would fetch
		// again and pull in additional nodes via `scoreExplore` /
		// `expandLayout`). Re-clicks should only re-run the magnetic
		// pull-in animation against the EXISTING landing children.
		const expanded = new Set(get(ExpandedNodeIdsAtom) as Set<string>);
		expanded.add(rootAri);
		set(ExpandedNodeIdsAtom, expanded);

		set(LayoutStatusAtom, 'ready');
	} catch (err) {
		set(LayoutStatusAtom, 'error');
		set(LayoutErrorAtom, (err as Error)?.message ?? 'Failed to load graph');
	}
});

// ============================================================================
// Expand — Mode B click-to-expand
// ============================================================================

/**
 * Expand a previously-placed node. If already expanded, this is a no-op.
 * Concurrent expansion of the same node is de-duplicated.
 */
export const ExpandNodeAtom: WritableAtom<null, [nodeId: string], Promise<void>> & {
	init: null;
} = atom(null, async (get, set, nodeId: string) => {
	const inflight = get(ExpandingNodeIdsAtom) as Set<string>;
	if (inflight.has(nodeId)) {
		return;
	}
	const expanded = get(ExpandedNodeIdsAtom) as Set<string>;
	if (expanded.has(nodeId)) {
		return;
	}

	const layoutNodes = get(LayoutNodesAtom) as Map<string, LayoutNode>;
	const parent = layoutNodes.get(nodeId);
	if (!parent) {
		return;
	}

	const nextInflight = new Set(inflight);
	nextInflight.add(nodeId);
	set(ExpandingNodeIdsAtom, nextInflight);
	set(LayoutStatusAtom, 'expanding');

	try {
		const hydrated = get(HydratedNodesAtom) as Map<string, DisplayNode>;
		const parentDisplay = hydrated.get(nodeId);
		const parentNodeData: ObjectNodeData = parentDisplay
			? {
					id: parentDisplay.id,
					typename: parentDisplay.typename,
					typenameDisplay: parentDisplay.typenameDisplay,
					displayName: parentDisplay.displayName,
					url: parentDisplay.url,
					properties: parentDisplay.properties,
					productIcon: parentDisplay.productIcon ?? undefined,
					productIconImportPath: parentDisplay.productIconImportPath ?? undefined,
				}
			: {
					id: nodeId,
					typename: parent.sizingInput.typename,
					displayName: parent.sizingInput.title,
					url: null,
					properties: {},
				};

		await set(DiscoverForNodeAtom, parentNodeData);

		if (get(DiscoveryStatusAtom) === 'error') {
			set(LayoutStatusAtom, 'error');
			set(LayoutErrorAtom, 'Expansion failed');
			return;
		}

		const outgoing = get(DiscoveredOutgoingAtom) as RelationshipInfo[];
		const incoming = get(DiscoveredIncomingAtom) as RelationshipInfo[];
		const relMap = await get(twgRelationshipsAsyncAtom);

		const allDiscovered: DiscoveredNode[] = [];
		for (const rel of [...outgoing, ...incoming]) {
			for (const n of rel.nodes ?? []) {
				allDiscovered.push(n);
			}
		}
		mergeDisplayNodes(set, get, allDiscovered, undefined);

		const candidates = flattenToCandidates(outgoing, incoming, relMap, nodeId);
		// Algorithm choice is driven by the parent node's typename:
		//   • User/person parent → `scoreForYou` (the same "what's
		//     relevant for *this* user" algorithm we use at landing).
		//   • Anything else      → `scoreExplore` (broad-typename
		//     exploration suited to documents, work items, etc.).
		// This keeps re-clicks on a user node consistent with the
		// landing experience and prevents the wrong algorithm from
		// pulling in a different / larger neighbour set.
		const parentIsUser = isUserTypename(parent.sizingInput.typename);
		const selection = parentIsUser
			? // eslint-disable-next-line @typescript-eslint/no-explicit-any
				scoreForYou(candidates as any, { totalSlots: 12 })
			: scoreExplore(candidates, {
					sourceTypename: parent.sizingInput.typename,
					totalSlots: 12,
				});

		const freshHydrated = get(HydratedNodesAtom) as Map<string, DisplayNode>;
		const layoutChildren = toLayoutChildren(selection.kept, freshHydrated);
		const delta = expandLayout(nodeId, layoutChildren, layoutNodes);
		const merged = applyDelta(layoutNodes, get(CrossEdgesAtom) as CrossEdge[], delta);
		set(LayoutNodesAtom, merged.nodes);
		set(CrossEdgesAtom, merged.crossEdges);

		// Stamp recency for the parent + every freshly-arrived child.
		// The parent is bumped because it just became the "interesting"
		// node; the children get a baseline so they can be hide-
		// candidates next time around.
		const now = Date.now();
		const recency = new Map(get(NodeRecencyAtom));
		recency.set(nodeId, now);
		for (const n of delta.added) {
			recency.set(n.id, now);
		}
		set(NodeRecencyAtom, recency);

		// Run the overlap evaluation: any new node that significantly
		// overlaps an older one hides the older one.
		if (delta.added.length > 0) {
			const addedIds = delta.added.map((n) => n.id);
			set(EvaluateOverlapHidesAtom, addedIds);
			// Mark every fresh child as "appearing" so the node
			// renderer fades them in while the magnetic pull brings
			// existing siblings toward the clicked parent.
			set(MarkAppearingAtom, addedIds);
		}

		const nextExpanded = new Set(expanded);
		nextExpanded.add(nodeId);
		set(ExpandedNodeIdsAtom, nextExpanded);
		set(LayoutStatusAtom, 'ready');
	} catch (err) {
		set(LayoutStatusAtom, 'error');
		set(LayoutErrorAtom, (err as Error)?.message ?? 'Expansion failed');
	} finally {
		const done = new Set(get(ExpandingNodeIdsAtom) as Set<string>);
		done.delete(nodeId);
		set(ExpandingNodeIdsAtom, done);
	}
});

// ============================================================================
// Collapse
// ============================================================================

// ============================================================================
// Expand-cluster — promotes a cluster node's hidden members
// ============================================================================

// ============================================================================
// Reset
// ============================================================================

// ============================================================================
// Blur (pane click) — clear transient selection state, no layout mutation
// ============================================================================

/**
 * Pane-click "blur" handler. Previously this ran a destructive
 * `relaxAroundCenter` pass that rewrote every node's atom rest
 * position to resolve overlaps it detected in atom space — which
 * caused nodes to be ejected far from their original positions
 * because atom positions diverge from visual positions while a
 * magnetic pull-in ring is active.
 *
 * The new design keeps Selected mode and Discovery mode strictly
 * isolated:
 *   - Selected mode only mutates transient `dispX/dispY` (visual
 *     displacement) via `setMagneticTargets`.
 *   - Discovery mode shows atom rest positions exactly as
 *     `expandLayout` placed them. Pane click clears any active
 *     magnetic targets and the spring carries every node back to
 *     its permanent rest position.
 *
 * Overlap avoidance is now the responsibility of `placeAroundCenter`
 * at expansion time (when new nodes are first placed), not of any
 * post-hoc relaxation pass.
 *
 * The `center` parameter is retained for backward compatibility but
 * is unused; callers can omit it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- center kept for backward-compatible callers
export const RelaxOnBlurAtom: WritableAtom<null, [_center?: {
    x: number;
    y: number;
} | undefined], void> & {
    init: null;
} = atom(null, (_get, set, _center?: { x: number; y: number }) => {
	// Clearing the hidden-nodes set is the only mutation pane click
	// still performs. Any nodes hidden by the overlap-hides evaluation
	// while a selection was active should reappear in Discovery mode.
	set(HiddenNodesAtom, new Set<string>());
});

/**
 * Typename-keyed size estimate used by blur relax and the magnetic
 * pull-in ring sizing. Mirrors `IncrementalRadialLayout::estimateSize`
 * — kept in sync so the pull-in ring lands on the same spacing the
 * layout engine produces. The values don't need to be perfectly
 * identical to the ReactFlow-measured DOM sizes: a mild over-estimate
 * just biases toward slightly more breathing room.
 */
export function estimateLayoutSize(node: LayoutNode): NodeSize {
	return estimateLayoutNodeSize(node.sizingInput);
}

export const ResetLayoutAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	set(LayoutNodesAtom, new Map());
	set(CrossEdgesAtom, []);
	set(HydratedNodesAtom, new Map());
	set(ExpandingNodeIdsAtom, new Set());
	set(ExpandedNodeIdsAtom, new Set());
	set(LayoutStatusAtom, 'idle');
	set(LayoutErrorAtom, null);
	set(NodeRecencyAtom, new Map());
	set(HiddenNodesAtom, new Set<string>());
	set(AppearingNodesAtom, new Map());
});
